import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';

function Framework() {
  const [isLoading, setIsLoading] = useState(true);
  const [frameworks, setFrameworks] = useState([]);
  const [userFramework, setUserFramework] = useState(null);
  const [mcpServer, setMcpServer] = useState(null);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState('');
  const [mcpConfig, setMcpConfig] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    const fetchFrameworkData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/frameworks/user`);
        
        setFrameworks(response.data.frameworks || []);
        setUserFramework(response.data.userFramework);
        setMcpServer(response.data.mcpServer);
        
        // Set default selected framework if user has one configured
        if (response.data.userFramework) {
          setSelectedFrameworkId(response.data.userFramework.framework_id);
          
          // Parse MCP config from user's saved configuration
          try {
            const config = typeof response.data.userFramework.mcp_config === 'string' 
              ? JSON.parse(response.data.userFramework.mcp_config) 
              : response.data.userFramework.mcp_config;
            
            setMcpConfig(config || {});
          } catch (e) {
            console.error('Error parsing MCP config:', e);
            setMcpConfig({});
          }
        }
      } catch (err) {
        console.error('Error fetching framework data:', err);
        setError('Failed to load framework data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFrameworkData();
  }, []);
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFrameworkId) {
      setError('Please select a framework');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setIsSubmitting(true);
      
      const configData = { ...mcpConfig };
      
      // Set a store ID if none exists
      if (!configData.store_id) {
        // Generate unique store ID using user-specific information
        const timestamp = new Date().getTime();
        configData.store_id = `user_${timestamp}_store`;
      }
      
      const response = await axios.post(`${API_URL}/frameworks/user`, {
        frameworkId: selectedFrameworkId,
        mcpConfig: configData
      });
      
      setSuccess('Framework settings saved successfully');
      
      // Refresh data after successful save
      setTimeout(async () => {
        const refreshResponse = await axios.get(`${API_URL}/frameworks/user`);
        setUserFramework(refreshResponse.data.userFramework);
        setMcpServer(refreshResponse.data.mcpServer);
      }, 1000);
    } catch (err) {
      console.error('Error saving framework:', err);
      setError(err.response?.data?.message || 'Error saving framework settings');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleFrameworkChange = (e) => {
    const newFrameworkId = e.target.value;
    setSelectedFrameworkId(newFrameworkId);
    
    // Reset MCP config when framework changes
    if (userFramework && userFramework.framework_id === parseInt(newFrameworkId)) {
      // If switching back to previously configured framework, restore saved config
      try {
        const config = typeof userFramework.mcp_config === 'string' 
          ? JSON.parse(userFramework.mcp_config) 
          : userFramework.mcp_config;
        
        setMcpConfig(config || {});
      } catch (e) {
        console.error('Error parsing saved MCP config:', e);
        setMcpConfig({});
      }
    } else {
      // New framework selected, initialize with store_id only
      setMcpConfig({ store_id: mcpConfig.store_id || '' });
    }
  };
  
  const handleConfigChange = (field, value) => {
    setMcpConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Get the selected framework's MCP server
  const getSelectedFrameworkMcp = () => {
    if (!selectedFrameworkId || !frameworks.length) return null;
    
    const selectedFramework = frameworks.find(f => f.id === parseInt(selectedFrameworkId));
    if (!selectedFramework) return null;
    
    return mcpServer;
  };
  
  const selectedMcp = getSelectedFrameworkMcp();
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading framework data...</p>
      </div>
    );
  }
  
  return (
    <Card className="shadow-sm">
      <Card.Header>
        <h5 className="m-0">Framework Configuration</h5>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        {frameworks.length === 0 ? (
          <Alert variant="warning">
            No active frameworks available. Please contact the administrator.
          </Alert>
        ) : (
          <>
            <div className="mb-4">
              <p>
                Select the e-commerce framework for your business. This determines which platform your AI chatbot will connect to.
              </p>
              
              {userFramework && (
                <Alert variant="info">
                  <i className="fas fa-info-circle me-2"></i>
                  You are currently using the <strong>{userFramework.framework_name}</strong> framework.
                </Alert>
              )}
            </div>
            
            <Form onSubmit={handleFormSubmit}>
              <Form.Group className="mb-4">
                <Form.Label>Select Framework</Form.Label>
                <Form.Select 
                  value={selectedFrameworkId} 
                  onChange={handleFrameworkChange}
                  required
                >
                  <option value="">-- Select Framework --</option>
                  {frameworks.map(framework => (
                    <option key={framework.id} value={framework.id}>
                      {framework.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              {selectedFrameworkId && selectedMcp && (
                <div className="mt-4">
                  <h5>Configuration for {frameworks.find(f => f.id === parseInt(selectedFrameworkId))?.name}</h5>
                  
                  {/* Store ID field */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Store ID <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={mcpConfig.store_id || ''}
                      onChange={(e) => handleConfigChange('store_id', e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted">
                      A unique identifier for your store. This must be unique per store connection.
                    </Form.Text>
                  </Form.Group>
                  
                  {/* Dynamic config fields based on framework's schema */}
                  {selectedMcp && selectedMcp.config_schema && (() => {
                    try {
                      const schema = typeof selectedMcp.config_schema === 'string' 
                        ? JSON.parse(selectedMcp.config_schema) 
                        : selectedMcp.config_schema;
                      
                      return Object.entries(schema).map(([field, type]) => {
                        if (field === 'store_id') return null; // Skip store_id as we handle it separately
                        
                        return (
                          <Form.Group className="mb-3" key={field}>
                            <Form.Label>
                              {field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ')}
                            </Form.Label>
                            <Form.Control
                              type={type === 'password' ? 'password' : 'text'}
                              value={mcpConfig[field] || ''}
                              onChange={(e) => handleConfigChange(field, e.target.value)}
                            />
                          </Form.Group>
                        );
                      });
                    } catch (e) {
                      console.error('Error parsing config schema:', e);
                      return <Alert variant="danger">Error loading configuration schema</Alert>;
                    }
                  })()}
                </div>
              )}
              
              <div className="mt-4">
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={isSubmitting || !selectedFrameworkId}
                >
                  {isSubmitting ? 'Saving...' : 'Save Framework Settings'}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default Framework;
